<template>
  <k-modal :show="showModal" @close="closeEditDateModal" class="edit-date-modal">
    <template #header>
      <h3>Schedule Meeting</h3>
    </template>
    <template #body>
      <include-line-manager-toggle
        v-model="includeLineManager"
        :line-manager="lineManager"
      />
      <div
        class="profile-cards-row"
        :class="{ 'profile-cards-row--split': showLineManagerCard }"
      >
        <user-profile-card
          class="learner-profile-card"
          :name="meeting.student_name"
          :user-id="meeting.student_id"
          role="learner"
          :details="learnerProfileDetails"
        />
        <user-profile-card
          v-if="showLineManagerCard"
          class="line-manager-card"
          :name="lineManager.name"
          :email="lineManager.email"
          role="Line Manager"
          :userId="lineManager.user_id"
        />
      </div>
      <k-form>
        <set-meeting-datetime
          v-model="schedule"
          @validation="onDatetimeValidation"
        />
        <set-meeting-link
          v-model="meetingLink"
          input-id="meeting-link"
          :learner-user-id="meeting.student_id"
          :module-meeting-id="meeting.module_meeting_id"
          :start-time="meetingStartDate"
          :end-time="meetingEndDate"
          @validation="meetingLinkValid = $event.isValid"
          @teams-metadata-changed="onTeamsMetadataChanged"
        />
        <meeting-description-section v-model="meetingDescription" />
      </k-form>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="modal-return-button btn btn-outlined cancel-btn" @click="closeEditDateModal">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <button v-if="showUnscheduleButton"
                class="btn btn-danger"
                @click="unscheduleMeeting">Unschedule Meeting</button>
        <button class="btn btn-success submit-btn" :disabled="disableSubmit" @click="scheduleMeeting()">
          Schedule Meeting
        </button>
      </div>
    </template>
  </k-modal>
</template>

<style>
.edit-date-modal .modal-container {
  max-width: 60vw;
}

.edit-date-modal .modal-body {
  min-height: 30vh;
}

.edit-date-modal .user-profile-card {
  margin: 0 0 10px;
}
</style>

<style scoped>
.profile-cards-row {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

.profile-cards-row:not(.profile-cards-row--split) .learner-profile-card {
  width: 100%;
}

.profile-cards-row--split .learner-profile-card,
.profile-cards-row--split .line-manager-card {
  flex: 1;
  min-width: 0;
  width: 50%;
}

</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KModal from '@base-components/k-modal.vue';
import { KForm } from '@base-components/forms';
import UserProfileCard from '@base-components/user-profile-card.vue';
import IncludeLineManagerToggle from './include-line-manager-toggle.vue';
import SetMeetingDatetime from './set-meeting-datetime.vue';
import SetMeetingLink from './set-meeting-link.vue';
import MeetingDescriptionSection from './meeting-description-section.vue';
import isValidLineManager from '../utils/line-manager-utils';

export default {
  name: 'meeting-scheduler',
  components: {
    KModal,
    KForm,
    UserProfileCard,
    IncludeLineManagerToggle,
    SetMeetingDatetime,
    SetMeetingLink,
    MeetingDescriptionSection,
  },
  mixins: [TimeMixin],

  data() {
    return {
      schedule: {
        meetingDate: new Date(),
        startTime: new Date(),
        endTime: new Date(),
      },
      datetimeValid: false,
      meetingLink: '',
      meetingLinkValid: false,
      meetingDescription: '',
      includeLineManager: false,
      teamsMeetingId: null,
      teamsOrganiserUserId: null,
    };
  },

  props: {
    userId: {
      type: Number,
      required: true,
    },
    meeting: {
      type: Object,
      required: true,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    showUnscheduleButton: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    showModal(isShown) {
      if (isShown) {
        const meetingDate = this.meeting.meeting_start ? this.getDate(this.meeting.meeting_start) : new Date();
        const startTime = this.meeting.meeting_start ? this.getDate(this.meeting.meeting_start) : new Date();
        this.schedule = {
          meetingDate,
          startTime,
          endTime: this.meeting.meeting_end ? this.getDate(this.meeting.meeting_end) : null,
        };
        this.meetingLink = this.meeting.meeting_link || '';
        this.meetingDescription = this.meeting.custom_description || '';
        this.teamsMeetingId = null;
        this.teamsOrganiserUserId = null;
        this.includeLineManager = this.$featureFlags.isEnabled('meetings-inbox-gcal-integration')
          && this.hasLineManager
          && (this.meeting.include_line_manager ?? false);
      }
    },
  },

  computed: {
    meetingStartDate() {
      if (!this.schedule.meetingDate || !this.schedule.startTime) {
        return null;
      }
      return this.overrideDateComponent(this.schedule.startTime, this.schedule.meetingDate);
    },
    meetingEndDate() {
      if (!this.schedule.meetingDate || !this.schedule.endTime) {
        return null;
      }
      return this.overrideDateComponent(this.schedule.endTime, this.schedule.meetingDate);
    },
    disableSubmit() {
      return !this.meetingLinkValid || !this.datetimeValid;
    },
    learnerProfileDetails() {
      if (!this.meeting.programme_name) {
        return [];
      }
      return [{ label: 'Programme', value: this.meeting.programme_name }];
    },
    hasLineManager() {
      return isValidLineManager(this.meeting.line_manager);
    },
    lineManager() {
      return this.hasLineManager ? this.meeting.line_manager : null;
    },
    showLineManagerCard() {
      return this.$featureFlags.isEnabled('meetings-inbox-gcal-integration')
        && this.includeLineManager
        && this.lineManager;
    },
  },

  methods: {
    closeEditDateModal() {
      this.$emit('close');
    },

    onTeamsMetadataChanged(metadata) {
      this.teamsMeetingId = metadata?.teamsMeetingId ?? null;
      this.teamsOrganiserUserId = metadata?.teamsOrganiserUserId ?? null;
    },

    onDatetimeValidation({ isValid }) {
      this.datetimeValid = isValid;
    },

    unscheduleMeeting() {
      this.$ktoast.confirmToast(
        `Are you sure you want to unschedule <b>${this.meeting.meeting_name}</b> meeting?<br><br>It will be added back under the "Meetings to Schedule" tab.`,
        'warning',
        () => {
          this.unscheduleMeetingCallback();
        },
      );
    },

    unscheduleMeetingCallback() {
      this.$Loading.start();
      this.$http.put(`/api/curriculum/admin/module/meetings/user/${this.meeting.user_module_meeting_id}/unschedule`)
        .then(() => {
          this.$ktoast.success('Meeting unscheduled successfully');
          this.$emit('update');
          this.closeEditDateModal();
        })
        .catch(err => {
          this.$logger.error('Error unscheduling meeting', { meetingId: this.meeting }, err);
          this.$ktoast.showError(err, true);
        }).then(() => {
          this.$Loading.finish();
        });
    },

    scheduleMeeting() {
      const payload = {
        meeting_link: this.meetingLink,
        description: this.meetingDescription,
        meeting_start: this.formatDate(this.meetingStartDate),
        meeting_end: this.formatDate(this.meetingEndDate),
        meeting_name: this.meeting.meeting_name,
        meeting_owner_user_id: this.userId,
        meeting_owner_role: this.meeting.support_function,
        is_progress_review: true,
        include_line_manager: this.$featureFlags.isEnabled('meetings-inbox-gcal-integration') && this.includeLineManager,
        ...(this.teamsMeetingId && {
          teams_meeting_id: this.teamsMeetingId,
          teams_organiser_user_id: this.teamsOrganiserUserId,
        }),
      };

      this.$logger.info('Scheduling meeting', { meetingId: this.meeting });
      const moduleMeetingId = this.meeting.module_meeting_id;
      const moduleId = this.meeting.module_id;
      const studentId = this.meeting.student_id;
      this.$Loading.start();
      this.$http.put(`/api/curriculum/admin/module/${moduleId}/meetings/${moduleMeetingId}/user/${studentId}/schedule`, payload)
        .then(() => {
          this.$ktoast.success('Meeting scheduled successfully');
          this.$emit('update');
          this.closeEditDateModal();
        })
        .catch(err => {
          this.$logger.error('Error scheduling meeting', { meetingId: this.meeting }, err);
          this.$ktoast.showError(err, false);
        }).then(() => {
          this.$Loading.finish();
        });
    },
  },

};
</script>
